import _ from 'lodash'
import { fromJS, Map } from 'immutable'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { makeGridTemplate } from '../../../constants/makeGridTemplate'
import { useLayoutMode } from '../../../hooks/useLayoutMode'
import { useGridAreaForDevice, useGridAreaStyleRules, useGridSettings } from '../../../hooks/useValueForDevice'
import { useComponentStyle, useProjectComponent } from '../../../hooks/useWidgetComponent'
import { StyleElement } from '../../Main/StyleElement'
import { isSame } from './isSame'
import { PREVIEW_PAGE_ID } from '../../../constants/PREVIEW_PAGE_ID'

const getCol = (val, index) => _.map(val, (v) => v[index])
const setCol = (val, index, setVal) =>
  _.reduce(
    val,
    (r, v, i) => {
      r[i][index] = setVal[i]
      return r
    },
    Array.from(val)
  )

const FT_AREA_TOP = '__ft_top'
const FT_AREA_LEFT = '__ft_left'
const FT_AREA_RIGHT = '__ft_right'
const isMoveArea = (area) => [FT_AREA_TOP, FT_AREA_LEFT, FT_AREA_RIGHT].indexOf(area) !== -1
const isGrowArea = (area) => ['.', '', FT_AREA_TOP, FT_AREA_LEFT, FT_AREA_RIGHT].indexOf(area) !== -1

//hide -> hide-move-* -> grow
const processAreaTransform = (nextArea, areaRules) => {
  let areas = Array.from(nextArea)
  const plainAreas = _.uniq(_.compact(_.flatten(areas)))
  _.each(plainAreas, (area) => {
    const rules = areaRules[area] ?? []
    if (rules.indexOf('hide') !== -1) {
      areas = _.chunk(
        _.map(_.flatten(areas), (a) => (a === area ? '.' : a)),
        areas[0].length
      )
    } else if (rules.indexOf('hide-move-top') !== -1) {
      areas = _.chunk(
        _.map(_.flatten(areas), (a) => (a === area ? FT_AREA_TOP : a)),
        areas[0].length
      )
    } else if (rules.indexOf('hide-move-left') !== -1) {
      areas = _.chunk(
        _.map(_.flatten(areas), (a) => (a === area ? FT_AREA_LEFT : a)),
        areas[0].length
      )
    } else if (rules.indexOf('hide-move-right') !== -1) {
      areas = _.chunk(
        _.map(_.flatten(areas), (a) => (a === area ? FT_AREA_RIGHT : a)),
        areas[0].length
      )
    }
  })

  _.each(plainAreas, (area) => {
    const status = areaRules[area] ?? []
    if (status.indexOf('grow') === -1)
      for (let i = 0; i < areas.length; i++) {
        const areaStartIndex = _.findIndex(areas[i], (a) => a === area)
        const areaEndIndex = _.findLastIndex(areas[i], (a) => a === area)
        if (areaStartIndex === areaEndIndex) {
          let col = getCol(areas, areaStartIndex)
          const areaStart = _.findIndex(col, (c) => c === area)
          if (isMoveArea(col[areaStart - 1])) {
            for (let x = areaStart - 1; x >= 0; x--) {
              if (isMoveArea(col[x])) {
                col[x] = ''
              }
            }
            col = _.compact(col)
            areas = setCol(
              areas,
              areaStartIndex,
              _.concat(
                [],
                col,
                _.times(areas[0].length - col.length, (n) => '.')
              )
            )
          }
        } else if (areaStartIndex !== -1 && areaEndIndex !== -1) {
          for (let x = areaStartIndex; x <= areaEndIndex; x++) {
            let col = getCol(areas, x)
            const areaStart = _.findIndex(col, (c) => c === area)
            if (isMoveArea(col[areaStart - 1])) {
              for (let x = areaStart - 1; x >= 0; x--) {
                if (isMoveArea(col[x])) {
                  col[x] = ''
                }
              }
              col = _.compact(col)
              areas = setCol(
                areas,
                x,
                _.concat(
                  [],
                  col,
                  _.times(areas[0].length - col.length, (n) => '.')
                )
              )
            }
          }
        }
      }
  })
  areas = growGridArea(areaRules, areas)
  areas = _.chunk(
    _.map(_.flatten(areas), (a) => (isMoveArea(a) ? '.' : a)),
    _.get(areas, [0], []).length
  )
  return fromJS(areas)
}

function growGridArea(areaRules, areas) {
  let nextArea = Array.from(areas)
  _.each(areaRules, (status, area) => {
    if (status.indexOf('grow') !== -1) {
      for (let i = 0; i < nextArea.length; i++) {
        const areaStartIndex = _.findIndex(nextArea[i], (a) => a === area)
        const areaEndIndex = _.findLastIndex(nextArea[i], (a) => a === area)
        if (areaStartIndex === areaEndIndex && areaStartIndex !== -1) {
          let isGrowTop = false
          for (let x = i - 1; x >= 0; x--) {
            if (isGrowArea(nextArea[x][areaStartIndex])) {
              nextArea[x][areaStartIndex] = area
              isGrowTop = true
            } else {
              break
            }
          }
          for (let x = i + 1; x < nextArea.length; x++) {
            if (isGrowArea(nextArea[x][areaStartIndex])) {
              nextArea[x][areaStartIndex] = area
              isGrowTop = true
            } else {
              break
            }
          }
          if (!isGrowTop) {
            const { left, right } = getGrowUntil(areaStartIndex, areaEndIndex, nextArea, area)
            if (areaStartIndex > left) {
              for (let from = areaStartIndex - 1; from >= left; from--) {
                nextArea[i][from] = area
              }
            }
            if (areaEndIndex < right) {
              for (let to = areaEndIndex + 1; to <= right; to++) {
                nextArea[i][to] = area
              }
            }
          }
        } else if (areaStartIndex !== -1 && areaEndIndex !== -1) {
          if (areaStartIndex > 0) {
            for (let x = areaStartIndex - 1; x >= 0; x--) {
              if (isGrowArea(nextArea[i][x])) {
                nextArea[i][x] = area
              } else {
                break
              }
            }
          }

          if (areaEndIndex !== -1) {
            for (let x = areaEndIndex + 1; x < nextArea[i].length; x++) {
              if (nextArea[i][areaEndIndex] === area || nextArea?.[i + 1]?.[areaEndIndex]) {
                break
              }
              if (nextArea[i][x] === '.') {
                nextArea[i][x] = area
              } else {
                break
              }
            }
          }
        }
      }
    }
  })

  return nextArea
}

function getGrowUntil(startIndex, endIndex, areas, area) {
  let left = {}
  let right = {}
  for (let row = 0; row < areas.length; row++) {
    if (areas[row][startIndex] === area) {
      for (let col = startIndex - 1; col >= 0; col--) {
        if (isGrowArea(areas[row][col])) {
          left[row] = col
        } else {
          if (col === startIndex - 1) {
            left[-1] = startIndex
          }
          break
        }
      }
    }
    if (areas[row][endIndex] === area) {
      for (let col = endIndex + 1; col < areas[0].length; col++) {
        if (isGrowArea(areas[row][col])) {
          right[row] = col
        } else {
          if (col === endIndex + 1) {
            right[-1] = endIndex
          }
          break
        }
      }
    }
  }

  return {
    left: _.max(_.values(left)) ?? startIndex,
    right: _.min(_.values(right)) ?? endIndex,
  }
}

export const useStripAreaData = (isPreview, PageID, uid, styleRef) => {
  const mode = useLayoutMode()
  const { component } = useProjectComponent(uid, PageID)
  const { componentStyles } = useComponentStyle(uid, PageID)
  const isRepeat = component.getIn(['props', 'isRepeat'], false)
  const isRepeatCol = component.getIn(['props', 'isRepeatCol'], false)
  const repeatColName = component.getIn(['props', 'repeatColName'])

  const [columns, rows] = useGridSettings(styleRef, PageID)
  const areas = useGridAreaForDevice(styleRef, columns, rows, PageID)
  const gridTemplateRowsOrigin = useMemo(
    () => makeGridTemplate(component, componentStyles, mode, uid, columns, rows, 'row'),
    [component, componentStyles, mode, uid, columns, rows]
  )

  const gridTemplateColumns = useMemo(() => {
    const cols = makeGridTemplate(component, componentStyles, mode, uid, columns, rows, 'column', isRepeatCol)
    if (isRepeatCol) {
      const row = areas.toJS()[0]
      if (_.isArray(row)) {
        let width = String(cols).split(' ')[row.indexOf(repeatColName)] || '100%'
        if (String(width).indexOf('fr') !== -1) {
          return '100%'
        } else {
          return width
        }
      } else {
        return cols
      }
    } else {
      return cols
    }
  }, [component, componentStyles, mode, uid, columns, rows, areas, isRepeatCol, repeatColName])

  const gridAreaStyleRules = useGridAreaStyleRules(styleRef, PageID, uid)

  const areasPatched = useMemo(() => {
    if (!isPreview) {
      return areas
    }
    let nextArea = areas.toJS()
    const hiddenRows = component.getIn(['props', mode], Map()).reduce((r, v, k) => {
      if (_.startsWith(k, 'isHidden') && k !== 'isHidden' && v) {
        return [...r, k.replace('isHidden', '')]
      } else {
        return r
      }
    }, [])
    // uid === 's5' && console.log('begin area transform >>>', areas.toJS(), gridAreaStyleRules, hiddenRows)
    _.each(hiddenRows, (col) => {
      if (_.isArray(gridAreaStyleRules[col])) {
        gridAreaStyleRules[col].push('hide')
      } else {
        gridAreaStyleRules[col] = ['hide']
      }
    })
    // console.table(areas.toJS())
    if (nextArea && nextArea[0] && nextArea[0].length === 1) {
      const hide = _.compact(
        _.map(gridAreaStyleRules, (status, area) => {
          if (status.indexOf('hide') !== -1) {
            return area
          }
        })
      )
      return fromJS(_.filter(nextArea, (rows) => hide.indexOf(rows[0]) === -1))
    } else {
      nextArea = processAreaTransform(nextArea, gridAreaStyleRules)
    }
    return fromJS(nextArea)
  }, [areas, gridAreaStyleRules, mode])

  const areasStr = useMemo(() => {
    let areas = areasPatched.toJS()
    return `${_.map(
      _.map(areas, (row) => row.join(' ')),
      (row) => `"${row}"`
    ).join('\n')}`
    // }
  }, [areasPatched, mode /*, isRTL, mode*/])

  const gridTemplateRows = useMemo(() => {
    if (isRepeatCol) {
      const rows = String(gridTemplateRowsOrigin).split(' ')
      if (String(rows[0]).indexOf('px') !== -1) {
        return rows[0]
      } else {
        return '250px'
      }
    }
    if (['lg', 'md', 'sml'].indexOf(mode) === -1) {
      const plainAreas = areas.toJS()
      const plainAreasPatched = areasPatched.toJS()
      let rows = String(gridTemplateRowsOrigin).split(' ')
      const diff = _.difference(_.flatten(plainAreas), _.flatten(plainAreasPatched))
      _.each(diff, (col) => {
        const rowIndex = _.findIndex(plainAreas, (row) => row[0] === col)
        if (rowIndex !== -1) {
          rows.splice(rowIndex, 1)
        }
      })
      if (isRepeat) {
        rows = _.map(rows, (row) => {
          if (String(row).indexOf('px') !== -1) {
            return `minmax(${row},auto)`
          } else {
            return row
          }
        })
      }
      if (rows.length !== areasPatched.size) {
        return _.slice(rows, 0, areasPatched.size).join(' ')
      } else {
        return rows.join(' ')
      }
    } else {
      let rows = String(gridTemplateRowsOrigin).split(' ')
      if (rows.length !== areasPatched.size) {
        return _.slice(rows, 0, areasPatched.size).join(' ')
      } else {
        return gridTemplateRowsOrigin
      }
    }
  }, [gridTemplateRowsOrigin, areasPatched, isRepeatCol, isRepeat, areas, mode])

  return {
    areas,
    gridTemplateRowsOrigin,
    gridTemplateColumns,
    gridAreaStyleRules,
    areasPatched,
    areasStr,
    gridTemplateRows,
    columns,
    rows,
  }
}

const RenderStripClasses = ({ isPreview, PageID, uid, styleRef }) => {
  const { areasStr: areas, gridTemplateRows, gridTemplateColumns } = useStripAreaData(isPreview, PageID, uid, styleRef)
  const { componentStyles } = useComponentStyle(uid, PageID)
  const mode = useLayoutMode()

  const styleText = useMemo(() => {
    const prefix = `.ftst--${styleRef}`
    let rootStyle = `${mode === 'xs' || mode === 'ss' || mode === 'sm' ? `height: auto !important;min-height: 1px;max-height: unset;` : ''}`
    if (['xs', 'ss', 'sm'].includes(mode) && componentStyles.getIn([mode, 'root', 'minHeightUnit']) === '%') {
      rootStyle = `height: 100% !important;min-height: 1px;max-height: unset;`
    }
    return `${prefix}--root {
                    z-index: 2;
                    width: 100% !important;
                    position: initial;
                    display: grid;
                    grid-template-rows: ${mode === 'xs' || mode === 'ss' || mode === 'sm' ? gridTemplateRows : '1fr'};
                    grid-template-columns: ${mode === 'xs' || mode === 'ss' || mode === 'sm' ? '100%' : gridTemplateColumns};
                    grid-template-areas: ${areas};
                    grid-auto-rows: 1fr;
                    grid-auto-columns: 1fr;
                    grid-auto-flow: row;
                    overflow: visible;
                    ${rootStyle}
                }
                ${prefix}--root > * {z-index: 3;}
                ${prefix}--repeatRoot{
                    z-index: 2;
                    width: 100% !important;
                    position: initial;
                    display: flex;
                    flex-wrap: wrap;
                    ${
                      mode === 'xs' || mode === 'ss' || mode === 'sm'
                        ? `height: auto !important;min-height: unset;max-height: unset;`
                        : ''
                    }
                }
                ${prefix}--repeatPlaceholder{
                    display: inline-flex;
                    width: ${gridTemplateColumns};
                    min-height: ${gridTemplateRows};
                }
                ${prefix}--repeatColPlaceHolder{min-height: ${gridTemplateRows};}`
  }, [mode, areas, gridTemplateRows, gridTemplateColumns, componentStyles])

  return <StyleElement uid={`ftst--${styleRef} `} style={styleText} />
}

export const RenderStripStyles = ({ isPreview }) => {
  const PageID = useSelector((state) => state.get('page'))
  const devStripPreviewStyles = useSelector(
    (state) =>
      state.getIn(['pages', PREVIEW_PAGE_ID, 'Props', 'elements'], Map()).reduce((ret, elem, uid) => {
        const styleRef = elem.getIn(['props', 'styleRef'], uid)
        if (elem.get('type') !== 'Strip') {
          return ret
        } else {
          return ret.set(styleRef, uid)
        }
      }, Map()),
    isSame
  )
  const stripStyles = useSelector(
    (state) =>
      state.getIn(['pages', state.get('page'), 'Props', 'elements'], Map()).reduce((ret, elem, uid) => {
        const styleRef = elem.getIn(['props', 'styleRef'], uid)
        if (elem.get('type') !== 'Strip') {
          return ret
        } else {
          return ret.set(styleRef, uid)
        }
      }, Map()),
    isSame
  )
  const projectStripStyles = useSelector(
    (state) =>
      state.getIn(['project', 'Settings', 'elements'], Map()).reduce((ret, elem, uid) => {
        const styleRef = elem.getIn(['props', 'styleRef'], uid)
        if (elem.get('type') !== 'Strip') {
          return ret
        } else {
          return ret.set(styleRef, uid)
        }
      }, Map()),
    isSame
  )

  return (
    <>
      {devStripPreviewStyles
        .map((uid, styleRef) => (
          <RenderStripClasses isPreview key={styleRef} uid={uid} styleRef={styleRef} PageID={PREVIEW_PAGE_ID} />
        ))
        .toList()}
      {stripStyles
        .map((uid, styleRef) => (
          <RenderStripClasses isPreview={isPreview} key={styleRef} uid={uid} styleRef={styleRef} PageID={PageID} />
        ))
        .toList()}
      {projectStripStyles
        .map((uid, styleRef) => (
          <RenderStripClasses isPreview={isPreview} key={styleRef} uid={uid} styleRef={styleRef} PageID="project" />
        ))
        .toList()}
    </>
  )
}
